// Xuất MP4 hoàn chỉnh NGAY TRONG TRÌNH DUYỆT (ffmpeg.wasm) — server KHÔNG phải render/lưu video.
// Mỗi cảnh = 1 khung hình tĩnh (1280×720) + lời đọc (nếu đã tạo giọng). Ghép thành MP4 slideshow.
// Giới hạn MVP: mô phỏng/video nhúng hiển thị dạng thẻ nhãn trong MP4 (vẫn tương tác đầy đủ ở trình phát trong bài);
// công thức được chuyển sang chữ dễ đọc. Trình phát trong app vẫn là trải nghiệm tương tác đầy đủ.
import { FFmpeg } from '@ffmpeg/ffmpeg'
import { fetchFile, toBlobURL } from '@ffmpeg/util'
import type { LectureScene } from '../types/LessonDoc'

const W = 1280, H = 720
const CORE = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'

// Link YouTube? (không tải được bytes → không nhúng vào MP4, sẽ dùng khung tĩnh)
const ytId = (url?: string): string | null => {
  if (!url) return null
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/)
  return m ? m[1] : (/^[A-Za-z0-9_-]{11}$/.test(url) ? url : null)
}

// Đổi LaTeX/$…$ sang chữ dễ đọc (MVP — canvas không render KaTeX).
const latexToText = (s: string): string => {
  let t = (s || '').replace(/\$+/g, ' ')
  const map: [RegExp, string][] = [
    [/\\alpha/g, 'α'], [/\\beta/g, 'β'], [/\\gamma/g, 'γ'], [/\\delta/g, 'δ'], [/\\theta/g, 'θ'],
    [/\\lambda/g, 'λ'], [/\\mu/g, 'µ'], [/\\pi/g, 'π'], [/\\rho/g, 'ρ'], [/\\sigma/g, 'σ'],
    [/\\omega/g, 'ω'], [/\\varphi/g, 'φ'], [/\\phi/g, 'φ'], [/\\Delta/g, 'Δ'], [/\\Omega/g, 'Ω'],
    [/\\times/g, '×'], [/\\cdot/g, '·'], [/\\pm/g, '±'], [/\\approx/g, '≈'], [/\\neq/g, '≠'],
    [/\\leq/g, '≤'], [/\\geq/g, '≥'], [/\\rightarrow/g, '→'], [/\\sqrt\s*\{([^}]*)\}/g, '√($1)'],
    [/\\frac\s*\{([^}]*)\}\s*\{([^}]*)\}/g, '($1)/($2)'],
    [/\^\{([^}]*)\}/g, '^$1'], [/_\{([^}]*)\}/g, '_$1'],
    [/\\left|\\right|\\,|\\!|\\;/g, ''], [/\\[a-zA-Z]+/g, ''], [/[{}]/g, ''],
  ]
  for (const [re, rep] of map) t = t.replace(re, rep)
  return t.replace(/\s+/g, ' ').trim()
}

const loadImage = (src: string): Promise<HTMLImageElement> => new Promise((res, rej) => {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => res(img)
  img.onerror = rej
  img.src = src
})

// Vẽ chữ xuống dòng tự động, trả số dòng đã vẽ.
const wrapText = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lineH: number, maxLines = 6) => {
  const words = (text || '').split(/\s+/)
  let line = '', lines: string[] = []
  for (const w of words) {
    const test = line ? line + ' ' + w : w
    if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w } else line = test
    if (lines.length >= maxLines) break
  }
  if (line && lines.length < maxLines) lines.push(line)
  lines.forEach((ln, i) => ctx.fillText(ln, x, y + i * lineH))
  return lines.length
}

// Render 1 cảnh thành PNG (Uint8Array) bằng canvas 2D.
const renderFrame = async (scene: LectureScene): Promise<Uint8Array> => {
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H
  const ctx = cv.getContext('2d')!
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, W, H)
  ctx.fillStyle = '#f5f3ff'; ctx.fillRect(0, 0, W, 90)        // dải tiêu đề
  ctx.fillStyle = '#6d28d9'; ctx.font = 'bold 40px Arial, sans-serif'; ctx.textBaseline = 'top'
  wrapText(ctx, latexToText(scene.caption || ''), 60, 24, W - 120, 46, 1)

  ctx.fillStyle = '#1e293b'
  if (scene.visualType === 'image' && scene.imageSrc) {
    try {
      const img = await loadImage(scene.imageSrc)
      const scale = Math.min((W - 160) / img.width, (H - 320) / img.height, 1)
      const iw = img.width * scale, ih = img.height * scale
      ctx.drawImage(img, (W - iw) / 2, 120, iw, ih)
    } catch { ctx.font = '30px Arial'; ctx.fillText('(không tải được ảnh)', 60, 300) }
  } else if (scene.visualType === 'formula' && scene.formula) {
    ctx.font = 'bold 56px "Cambria Math", Georgia, serif'; ctx.textAlign = 'center'
    wrapText(ctx, latexToText(scene.formula), W / 2, 300, W - 160, 70, 3)
    ctx.textAlign = 'left'
  } else if (scene.visualType === 'sim') {
    ctx.font = 'bold 34px Arial'; ctx.fillStyle = '#7c3aed'; ctx.textAlign = 'center'
    ctx.fillText('▶ Mô phỏng tương tác', W / 2, 300)
    ctx.font = '24px Arial'; ctx.fillStyle = '#64748b'
    ctx.fillText('(xem & tương tác trong bài học)', W / 2, 360); ctx.textAlign = 'left'
  } else if (scene.visualType === 'video') {
    ctx.font = 'bold 34px Arial'; ctx.fillStyle = '#7c3aed'; ctx.textAlign = 'center'
    ctx.fillText('▶ Video khoa học', W / 2, 320); ctx.textAlign = 'left'
  } else if (scene.visualType === 'question' && scene.question) {
    ctx.font = 'bold 34px Arial'
    let y = 160 + wrapText(ctx, latexToText(scene.question.prompt), 80, 160, W - 160, 44, 3) * 44 + 20
    ctx.font = '30px Arial'
    scene.question.options.forEach((o, i) => {
      ctx.fillStyle = i === scene.question!.correctIndex ? '#059669' : '#334155'
      ctx.fillText(`${String.fromCharCode(65 + i)}. ${latexToText(o)}`, 100, y); y += 50
    })
  } else {
    ctx.font = '36px Arial'; ctx.textAlign = 'center'
    wrapText(ctx, latexToText(scene.caption || scene.voiceover || ''), W / 2, 280, W - 200, 50, 6)
    ctx.textAlign = 'left'
  }

  const blob: Blob = await new Promise((r) => cv.toBlob((b) => r(b!), 'image/png'))
  return new Uint8Array(await blob.arrayBuffer())
}

export interface ExportOpts { onProgress?: (msg: string) => void }

// Trả về Blob MP4 (gọi xong tự tạo link tải, KHÔNG đụng server).
export async function exportScenesToMp4(scenes: LectureScene[], opts: ExportOpts = {}): Promise<Blob> {
  const log = (m: string) => opts.onProgress?.(m)
  const usable = scenes.filter((s) => s.voiceover || s.caption || s.imageSrc || s.formula || s.question)
  if (!usable.length) throw new Error('Chưa có cảnh nào để xuất.')

  log('Đang tải bộ mã hoá video…')
  const ffmpeg = new FFmpeg()
  await ffmpeg.load({
    coreURL: await toBlobURL(`${CORE}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${CORE}/ffmpeg-core.wasm`, 'application/wasm'),
  })

  // Mọi segment cùng tham số codec để concat -c copy hợp lệ.
  const vEnc = ['-c:v', 'libx264', '-pix_fmt', 'yuv420p', '-vf', `scale=${W}:${H},fps=15`]
  const isDirectMp4 = (u?: string) => !!u && !ytId(u) && /^https?:\/\//.test(u)

  const segs: string[] = []
  for (let i = 0; i < usable.length; i++) {
    const s = usable[i]
    log(`Dựng cảnh ${i + 1}/${usable.length}…`)
    const seg = `s${i}.mp4`

    // Cảnh video có link .mp4 trực tiếp (vd clip Veo lưu trên server / link mp4 mở CORS) → nhúng clip thật + cắt đoạn.
    if (s.visualType === 'video' && isDirectMp4(s.videoUrl)) {
      try {
        await ffmpeg.writeFile(`v${i}.mp4`, await fetchFile(s.videoUrl!))
        const trim: string[] = []
        if (s.clipStart) trim.push('-ss', String(s.clipStart))
        if (s.clipEnd) trim.push('-to', String(s.clipEnd))
        await ffmpeg.exec([...trim, '-i', `v${i}.mp4`, ...vEnc, '-c:a', 'aac', '-b:a', '128k', '-ar', '44100', seg])
        segs.push(seg)
        continue
      } catch { /* tải/transcode lỗi (CORS…) → rơi xuống khung tĩnh */ }
    }

    // Còn lại: khung hình tĩnh (1 cảnh = 1 ảnh) + lời đọc nếu có.
    await ffmpeg.writeFile(`f${i}.png`, await renderFrame(s))
    const vIn = ['-loop', '1', '-framerate', '2', '-i', `f${i}.png`]
    if (s.audioUrl) {
      await ffmpeg.writeFile(`a${i}.mp3`, await fetchFile(s.audioUrl))
      await ffmpeg.exec([...vIn, '-i', `a${i}.mp3`, ...vEnc, '-tune', 'stillimage', '-c:a', 'aac', '-b:a', '128k', '-ar', '44100', '-shortest', seg])
    } else {
      const dur = Math.max(2, s.durationSec || 6)
      await ffmpeg.exec([...vIn, '-f', 'lavfi', '-i', 'anullsrc=channel_layout=stereo:sample_rate=44100',
        ...vEnc, '-tune', 'stillimage', '-c:a', 'aac', '-b:a', '128k', '-ar', '44100', '-t', String(dur), '-shortest', seg])
    }
    segs.push(seg)
  }

  log('Ghép MP4 hoàn chỉnh…')
  await ffmpeg.writeFile('list.txt', segs.map((s) => `file '${s}'`).join('\n'))
  await ffmpeg.exec(['-f', 'concat', '-safe', '0', '-i', 'list.txt', '-c', 'copy', 'out.mp4'])
  const data = (await ffmpeg.readFile('out.mp4')) as Uint8Array
  return new Blob([data as unknown as BlobPart], { type: 'video/mp4' })
}
